import React, { useEffect, useRef, useState, useCallback } from 'react'; // Added useCallback
import {
    Box, Button, Typography, Container, Stack, FormControl, InputLabel, Select, MenuItem, TextField, Chip, RadioGroup, FormControlLabel, Radio, IconButton,
    CircularProgress, Alert, // Import for search functionality feedback
    InputAdornment, // For search icon in TextField
    List,
    ListItemText
} from '@mui/material';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material'; // Import Close and Search icons

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import dbUserFromUser from '../auth/DataApi';
import { createActivity } from '../auth/MisBolosApi';

// --- Helper function for date formatting ---
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// --- Helper function for time formatting ---
const formatTime = (date) => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

// --- Image compression utility function ---
const compressImage = (file, maxSizeKb = 20) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                // Adjust dimensions if necessary to maintain aspect ratio and limit size
                const maxDim = 1200; // Max dimension for width or height
                if (width > maxDim || height > maxDim) {
                    if (width > height) {
                        height *= maxDim / width;
                        width = maxDim;
                    } else {
                        width *= maxDim / height;
                        height = maxDim;
                    }
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                let quality = 0.9; // Starting quality
                let dataUrl = canvas.toDataURL('image/jpeg', quality);
                let fileSize = dataUrl.length * (3 / 4) - 2; // Approximate size in bytes (base64 overhead)

                // Loop to reduce quality until file size is within limits
                while (fileSize > maxSizeKb * 1024 && quality > 0.1) {
                    quality -= 0.1;
                    dataUrl = canvas.toDataURL('image/jpeg', quality);
                    fileSize = dataUrl.length * (3 / 4) - 2;
                }

                resolve(dataUrl);
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    });
};

// --- MapUpdater Component (Modified) ---
const MapUpdater = ({ position, bounds }) => {
    const map = useMap();

    useEffect(() => {
        if (bounds) {
            map.fitBounds(bounds);
        } else if (position) {
            map.setView(position, map.getZoom());
        }
    }, [position, bounds, map]); // Add bounds to the dependency array

    return null;
};

// --- DrawControl Component ---
const DrawControl = ({ onDraw, zones }) => {
    const map = useMap();
    const drawnItemsRef = useRef(new L.FeatureGroup());

    useEffect(() => {
        const drawnItems = drawnItemsRef.current;
        map.addLayer(drawnItems);

        drawnItems.clearLayers();
        zones.forEach((polygonLatLngs) => {
            const polygon = L.polygon(polygonLatLngs);
            drawnItems.addLayer(polygon);
        });

        const drawControl = new L.Control.Draw({
            draw: {
                polygon: true,
                polyline: false,
                rectangle: false,
                circle: false,
                marker: false,
                circlemarker: false,
            },
            edit: { featureGroup: drawnItems }
        });

        map.addControl(drawControl);

        map.on(L.Draw.Event.CREATED, (e) => {
            const { layer } = e;
            drawnItems.clearLayers();
            drawnItems.addLayer(layer);
            const latlngs = layer.getLatLngs();
            onDraw(latlngs);
        });

        return () => {
            map.removeControl(drawControl);
            map.removeLayer(drawnItems);
        };
    }, [map, zones, onDraw]);

    return null;
};

// --- YouKnowFormScreen Component ---
const YouKnowFormScreen = () => {
    const { currentUser, isLoading, dbUser } = useAuth();
    const [userPosition, setUserPosition] = useState([43.3623, -8.3976]); // Default to A Coruña
    const [mapBounds, setMapBounds] = useState(null); // New state for map bounds
    const [zones, setZones] = useState([]);
    const [selectedLocal, setSelectedLocal] = useState('');
    const [useMap, setUseMap] = useState(false);
    const [addedGroups, setAddedGroups] = useState([]);
    const [groupInput, setGroupInput] = useState('');
    const [selectedExistingGroup, setSelectedExistingGroup] = useState('');

    const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
    const [duration, setDuration] = useState('');
    const [isPaid, setIsPaid] = useState('gratis');
    const [price, setPrice] = useState('');
    const [detailedInfo, setDetailedInfo] = useState('');

    const [title, setTitle] = useState('');

    const [images, setImages] = useState([]);

    // States for group search functionality
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchError, setSearchError] = useState('');

    const navigate = useNavigate();
    const mapRef = useRef();
    const fileInputRef = useRef(null);

    // Initial fetch for public groups (if needed, otherwise rely on search)
    // Removed the initial fetch for all groups as we'll now use the search
    // useEffect(() => {
    //     console.log('using effect with caution')
    //     if (!dbUser) return;
    //     const fetchGroups = async () => {
    //         try {
    //             const groups = await dbUser.getPublicGroups();
    //             setPublicGroups(groups);
    //             dbUser.publicGroups = groups
    //         } catch (error) {
    //             console.error("Failed to fetch public groups:", error);
    //         }
    //     };
    //     fetchGroups();
    // }, [dbUser]);

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                ({ coords }) => {
                    const newPosition = [coords.latitude, coords.longitude];
                    setUserPosition(newPosition);
                    setMapBounds(null); // Clear bounds if using current position
                },
                (err) => {
                    console.warn("Error getting geolocation:", err);
                }
            );
        } else {
            console.log("Geolocation is not supported by this browser.");
        }
    }, []);

    // New: Handle search for public groups
    const handleSearchGroups = useCallback(async () => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            setSearchError('');
            return;
        }

        setLoadingSearch(true);
        setSearchError('');
        try {
            // Call the getPublicGroups method from dbUser
            const response = (await dbUser.getPublicGroups({
                pageNum: 1,
                searchTerm: searchTerm,
                limit: 10
            })); // Assuming the response has a 'groups' property

            // Filter out groups that are already added
            const filteredResults = response.filter(
                (group) => !addedGroups.includes(group.name)
            );
            setSearchResults(filteredResults);
        } catch (error) {
            console.error('Error searching for groups:', error);
            setSearchError('Error al buscar grupos. Inténtalo de nuevo.');
        } finally {
            setLoadingSearch(false);
        }
    }, [searchTerm, addedGroups, dbUser]);

    // New: Debounce the group search input
    useEffect(() => {
        const handler = setTimeout(() => {
            if (dbUser) { // Ensure dbUser is available before searching
                handleSearchGroups();
            }
        }, 500); // 500ms delay

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm, handleSearchGroups, dbUser]); // Add dbUser to dependencies

    const handleNewZone = (latlngs) => {
        setZones([latlngs]);
        // When drawing a new zone, set the map to fit its bounds
        if (latlngs && latlngs.length > 0) {
            const bounds = L.polygon(latlngs).getBounds();
            setMapBounds(bounds);
            setUserPosition(null); // Clear position if using bounds
        }
    };

    const handleSelectLocal = (localName) => {
        if (localName === 'mapa') {
            setSelectedLocal('');
            setZones([]);
            setUseMap(true);
            setMapBounds(null); // No specific bounds when map drawing is active
            if (userPosition) {
                // If userPosition is already set, MapUpdater will use it
            } else {
                console.error('Fallback set position coruneno')
                setUserPosition([43.3623, -8.3976]); // A Coruña default
            }

        } else {
            const localsDict = dbUser?.misbolos?.properties || {};
            // Assuming misbolos.properties is an array of objects, not an object where keys are names
            const selected = Object.values(localsDict).find(l => l.name === localName);

            if (selected?.coordinates?.[0]) {
                console.log('selecting mode A')
                setZones([selected.coordinates]);
                const bounds = L.polygon(selected.coordinates).getBounds();
                setMapBounds(bounds); // Set bounds for MapUpdater
                setUserPosition(null); // Clear position if using bounds
            } else {
                console.log('selecting mode B', 'selected?', selected)
                setZones([]);
                setMapBounds(null);
                setUserPosition([43.3623, -8.3976]); // Default if coordinates are missing
            }
            setSelectedLocal(localName);
            setUseMap(false);
        }
    };

    const handleAddGroup = (groupName) => {
        const trimmedGroup = groupName.trim();
        if (trimmedGroup && !addedGroups.includes(trimmedGroup)) {
            setAddedGroups((prevGroups) => [...prevGroups, trimmedGroup]);
            setGroupInput(''); // Clear the input after adding
            setSearchTerm(''); // Clear search term and results
            setSearchResults([]);
        }
    };

    const handleDeleteGroup = (groupToDelete) => {
        setAddedGroups((prevGroups) => prevGroups.filter((group) => group !== groupToDelete));
    };

    const handleImageChange = async (event) => {
        const files = Array.from(event.target.files);
        const newImages = [...images];

        for (let i = 0; i < files.length && newImages.length < 3; i++) {
            const file = files[i];
            if (file.type.startsWith('image/')) {
                try {
                    const compressedDataUrl = await compressImage(file, 300);
                    newImages.push(compressedDataUrl);
                } catch (error) {
                    console.error("Error compressing image:", error);
                }
            }
        }
        setImages(newImages);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (indexToRemove) => {
        setImages((prevImages) => prevImages.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = async () => {
        const now = new Date();
        const endTime = new Date(now.getTime() + parseFloat(duration) * 60 * 60 * 1000);

        let eventKeyName = '';
        let eventLocationData = {};

        if (useMap) {
            eventKeyName = 'Mapa Drawn Zone ' + new Date().getTime();
            eventLocationData = { zones };
        } else {
            eventKeyName = selectedLocal;
            const localsDict = dbUser?.misbolos?.properties || {};
            const selected = Object.values(localsDict).find(l => l.name === selectedLocal);

            eventLocationData = {
                localId: selectedLocal,
                zones: zones,
                // Ensure center is correctly derived from the selected local's coordinates
                center: selected?.coordinates?.[0]?.[0] || userPosition // Fallback to user position if not found
            };
        }
        console.log('zones?', zones)
        const newEventData = {
            title: title,
            location: eventLocationData,
            date: selectedDate,
            timerange: {
                start: formatTime(now),
                end: formatTime(endTime)
            },
            groups: addedGroups,
            price: isPaid === 'pago' ? price : 'Gratis',
            images: images,
            name: eventKeyName,
            detailedInfo: detailedInfo
        };

        try {
            console.log("Submitting Group Data");
            const uid = {
                uid: dbUser.uid,
                id: dbUser._id
            }
            const ObjToSend = { user: uid, ...newEventData }
            console.log('ObjToSend?', ObjToSend, currentUser)
            const response = await dbUser.createActivity(newEventData)
            console.log('response?', response)
            // Optionally, navigate or show success message
        } catch (error) {
            console.error("Error submitting activity:", error);
            // Optionally, show error message to user
        }
        await dbUser.update();
        navigate('/misbolos/admin')
    };

    return (
        <Box sx={{ minHeight: '100vh', background: '#f0f4f8', py: 4 }}>
            <Container maxWidth="md">
                <Stack spacing={4}>
                    <Typography variant="h4" textAlign="center">Nueva actividad</Typography>
                    <TextField
                        label="Añadir Nombre Actividad"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        helperText="Utiliza un nombre único que haga fácil distinguirla de otras propiedades"
                        fullWidth
                        required
                        sx={{ mt: 2 }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="local-select-label">Selecciona tu local</InputLabel>
                        <Select
                            labelId="local-select-label"
                            value={selectedLocal || (useMap ? 'mapa' : '')}
                            onChange={(e) => handleSelectLocal(e.target.value)}
                            label="Selecciona tu local"
                        >
                            {dbUser?.misbolos?.properties &&
                                Object.values(dbUser.misbolos.properties).map((local) => (
                                    <MenuItem key={local.name} value={local.name}>
                                        {local.name}
                                    </MenuItem>
                                ))}
                            <MenuItem value="mapa">No tengo local / Quiero dibujar en el mapa</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden' }}>
                        <MapContainer
                            center={userPosition} // Still provide initial center
                            zoom={13}
                            whenCreated={(mapInstance) => { mapRef.current = mapInstance; }}
                            style={{ height: '100%' }}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OpenStreetMap contributors'
                            />
                            {/* Pass both position and bounds to MapUpdater */}
                            <MapUpdater position={userPosition} bounds={mapBounds} />
                            {useMap && <DrawControl onDraw={handleNewZone} zones={zones} />}
                            {!useMap && zones.length > 0 && (
                                <DrawControl onDraw={() => { }} zones={zones} />
                            )}
                        </MapContainer>
                    </Box>

                    {/* New: Group Search and Add Section */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>Añadir Grupos</Typography>
                        <TextField
                            label="Buscar o añadir grupo por nombre"
                            fullWidth
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') handleAddGroup(searchTerm); }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {loadingSearch ? <CircularProgress size={20} /> : <SearchIcon />}
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ mb: 1 }}
                            helperText="Empieza a escribir para buscar, o pulsa Enter para añadir un grupo nuevo"
                        />
                        {searchError && <Alert severity="warning" sx={{ mb: 2 }}>{searchError}</Alert>}

                        {searchTerm.trim() && searchResults.length > 0 && (
                            <Box sx={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, mb: 2 }}>
                                <List dense>
                                    {searchResults.map((group) => (
                                        <MenuItem
                                            key={group.id} // Assuming group object has an 'id'
                                            onClick={() => handleAddGroup(group.name)}
                                        >
                                            <ListItemText primary={group.name} secondary={group.description} />
                                        </MenuItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                        {!searchTerm.trim() && searchResults.length === 0 && (
                             <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                 Escribe para buscar grupos públicos o añade uno nuevo.
                             </Typography>
                         )}
                        {searchTerm.trim() && searchResults.length === 0 && !loadingSearch && !searchError && (
                            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                                No se encontraron grupos que coincidan con "{searchTerm}". Pulsa Enter para añadirlo como nuevo grupo.
                            </Typography>
                        )}
                       

                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {addedGroups.map((group, index) => (
                                <Chip
                                    key={index}
                                    label={group}
                                    onDelete={() => handleDeleteGroup(group)}
                                    color="primary"
                                />
                            ))}
                        </Box>
                    </Box>
                    {/* End New: Group Search and Add Section */}


                    <TextField
                        label="Fecha del evento"
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        InputLabelProps={{
                            shrink: true,
                        }}
                        fullWidth
                    />
                    <TextField
                        label="Detalles"
                        name="detailedInfo"
                        value={detailedInfo}
                        onChange={(e) => setDetailedInfo(e.target.value)}
                        fullWidth
                        multiline
                        rows={10}
                        variant="outlined"
                    />
                    <TextField
                        label="Duración (horas)"
                        type="number"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        inputProps={{ min: 0.5, step: 0.5 }}
                        fullWidth
                    />

                    <FormControl component="fieldset" fullWidth>
                        <Typography variant="subtitle1" gutterBottom>Precio de la entrada</Typography>
                        <RadioGroup
                            row
                            name="price-type"
                            value={isPaid}
                            onChange={(e) => setIsPaid(e.target.value)}
                        >
                            <FormControlLabel value="gratis" control={<Radio />} label="Gratis" />
                            <FormControlLabel value="pago" control={<Radio />} label="De pago" />
                        </RadioGroup>
                        {isPaid === 'pago' && (
                            <TextField
                                label="Importe (€)"
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                inputProps={{ min: 0 }}
                                fullWidth
                                sx={{ mt: 2 }}
                            />
                        )}
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle1" gutterBottom>Imágenes (máx 3, 500KB cada una)</Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => fileInputRef.current.click()}
                            disabled={images.length >= 3}
                            sx={{ mb: 2 }}
                        >
                            Añadir Imagen
                        </Button>
                        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
                            {images.map((imageSrc, index) => (
                                <Box key={index} sx={{ position: 'relative', width: 100, height: 100, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                                    <img
                                        src={imageSrc}
                                        alt={`Preview ${index + 1}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveImage(index)}
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                            backgroundColor: 'rgba(255,255,255,0.7)',
                                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                            p: '2px',
                                        }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Stack>
                    </Box>

                    <Button
                        variant="contained"
                        disabled={useMap && zones.length === 0}
                        onClick={handleSubmit}
                        sx={{ mt: 3 }}
                    >
                        Confirmar
                    </Button>
                </Stack>
            </Container>
        </Box>
    );
};

export default YouKnowFormScreen;